/**
 * Liquidity Pools
 *
 * Smart Money Concept: Identifies areas of stop-loss clusters
 * where swing highs/lows accumulate, creating liquidity targets.
 *
 * Category: Advanced | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const liquidityPoolsIndicator: IndicatorDefinition = {
  id: "liquidity_pools",
  name: "Liquidity Pools",
  shortName: "LIQ",
  category: "advanced",
  pane: "overlay",
  params: [
    { key: "lookback", label: "Lookback", type: "number", default: 20, min: 5, max: 100 },
    { key: "tolerance", label: "Tolerance %", type: "number", default: 0.2, min: 0.05, max: 2, step: 0.05 },
  ],
  plots: [
    { key: "highLiq", label: "High Liquidity", color: "#F44336", style: "circles", lineWidth: 4 },
    { key: "lowLiq", label: "Low Liquidity", color: "#4CAF50", style: "circles", lineWidth: 4 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { lookback, tolerance } = params;
    const highData: { time: number; value: number }[] = [];
    const lowData: { time: number; value: number }[] = [];

    // Collect swing highs and lows
    const swingHighs: { idx: number; value: number }[] = [];
    const swingLows: { idx: number; value: number }[] = [];

    for (let i = 2; i < candles.length - 2; i++) {
      if (candles[i].high > candles[i - 1].high && candles[i].high > candles[i - 2].high &&
          candles[i].high > candles[i + 1].high && candles[i].high > candles[i + 2].high) {
        swingHighs.push({ idx: i, value: candles[i].high });
      }
      if (candles[i].low < candles[i - 1].low && candles[i].low < candles[i - 2].low &&
          candles[i].low < candles[i + 1].low && candles[i].low < candles[i + 2].low) {
        swingLows.push({ idx: i, value: candles[i].low });
      }
    }

    // Cluster detection
    const tolPct = tolerance / 100;
    const findClusters = (swings: { idx: number; value: number }[]) => {
      const clusters: { idx: number; value: number; count: number }[] = [];
      const used = new Set<number>();

      for (let i = 0; i < swings.length; i++) {
        if (used.has(i)) continue;
        let count = 1;
        let sumValue = swings[i].value;
        let latestIdx = swings[i].idx;

        for (let j = i + 1; j < swings.length; j++) {
          if (used.has(j)) continue;
          if (Math.abs(swings[j].value - swings[i].value) / swings[i].value <= tolPct) {
            count++;
            sumValue += swings[j].value;
            if (swings[j].idx > latestIdx) latestIdx = swings[j].idx;
            used.add(j);
          }
        }

        if (count >= 2) {
          clusters.push({ idx: latestIdx, value: sumValue / count, count });
        }
      }
      return clusters;
    };

    const highClusters = findClusters(swingHighs);
    const lowClusters = findClusters(swingLows);

    for (const c of highClusters) {
      if (c.idx < candles.length) {
        highData.push({ time: candles[c.idx].time, value: c.value });
      }
    }
    for (const c of lowClusters) {
      if (c.idx < candles.length) {
        lowData.push({ time: candles[c.idx].time, value: c.value });
      }
    }

    return [
      { key: "highLiq", data: highData },
      { key: "lowLiq", data: lowData },
    ];
  },
};
